/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { List } from "../List";
import { useForkRef } from "../../../hooks";
import { isElement, isFunction, isHTMLElement } from "@adapptio/lib-shared";
import { getOwnerDocument } from "../../../utils";
import { Key } from "ts-key-enum";

export interface IMenuListProps extends React.ComponentPropsWithoutRef<"ul"> {
	children?: React.ReactNode;
	className?: string;
	variant?: "menu" | "selectedMenu";
	autoFocus?: boolean;
	autoFocusItem?: boolean;
	allowFocusDisabledItem?: boolean;
	disableListWrap?: boolean;
}

interface IMenuListTextCriteria {
	keys: string[];
	repeating: boolean;
	previousKeyMatched: boolean;
	lastTime: null | string;
}

type TMenuListTraversalFn = (
	list: HTMLUListElement,
	item: Element,
	disableListWrap: boolean
) => ChildNode | null;

const nextItem: TMenuListTraversalFn = (list, item, disableListWrap) => {
	if (list === item) {
		return list.firstChild;
	}
	if (item && item.nextElementSibling) {
		return item.nextElementSibling;
	}
	return disableListWrap ? null : list.firstChild;
};

const previousItem: TMenuListTraversalFn = (list, item, disableListWrap) => {
	if (list === item) {
		return disableListWrap ? list.firstChild : list.lastChild;
	}
	if (item && item.previousElementSibling) {
		return item.previousElementSibling;
	}
	return disableListWrap ? null : list.lastChild;
};

function moveFocus(
	list: HTMLUListElement,
	currentFocus: Element,
	disableListWrap: boolean,
	disabledItemsFocusable: boolean,
	traversalFunction: TMenuListTraversalFn,
	textCriteria?: IMenuListTextCriteria
) {
	let wrappedOnce = false;
	let nextFocus = traversalFunction(list, currentFocus, currentFocus ? disableListWrap : false);

	while (nextFocus) {
		// Prevent infinite loop.
		if (nextFocus === list.firstChild) {
			if (wrappedOnce) {
				return false;
			}
			wrappedOnce = true;
		}

		if (isElement(nextFocus)) {
			const nextFocusDisabled = disabledItemsFocusable
				? false
				: nextFocus["disabled"] || nextFocus.getAttribute("aria-disabled") === "true";

			if (
				!nextFocus.hasAttribute("tabindex") ||
				!textCriteriaMatches(nextFocus, textCriteria) ||
				nextFocusDisabled
			) {
				// Move to the next element.
				nextFocus = traversalFunction(list, nextFocus, disableListWrap);
			} else {
				if (isHTMLElement(nextFocus)) {
					nextFocus?.focus();
				}

				return true;
			}
		}
	}
	return false;
}

function textCriteriaMatches(nextFocus, textCriteria) {
	if (textCriteria === undefined) {
		return true;
	}

	let text = nextFocus.innerText;

	if (text === undefined) {
		text = nextFocus.textContent;
	}

	text = text.trim().toLowerCase();

	if (text.length === 0) {
		return false;
	}

	if (textCriteria.repeating) {
		return text[0] === textCriteria.keys[0];
	}

	return text.indexOf(textCriteria.keys.join("")) === 0;
}

const MenuList = React.forwardRef<HTMLUListElement, IMenuListProps>(
	(
		{
			children,
			className,
			variant,
			autoFocus,
			autoFocusItem,
			onKeyDown,
			allowFocusDisabledItem,
			disableListWrap,
			...props
		},
		ref
	) => {
		const baseClass = BEM("edt-menu-list");

		const listRef = useRef<HTMLUListElement | null>();
		const forkRef = useForkRef(ref, listRef);

		const textCriteriaRef = React.useRef({
			keys: [],
			repeating: true,
			previousKeyMatched: true,
			lastTime: null
		});

		const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLUListElement>>((event) => {
			const list = listRef.current;
			const key = event.key;

			const currentFocus = getOwnerDocument(list).activeElement;

			if (key === Key.ArrowDown) {
				// Prevent scroll of the page
				event.preventDefault();
				moveFocus(list, currentFocus, disableListWrap, allowFocusDisabledItem, nextItem);
			} else if (key === Key.ArrowUp) {
				event.preventDefault();
				moveFocus(list, currentFocus, disableListWrap, allowFocusDisabledItem, previousItem);
			} else if (key === Key.Home) {
				event.preventDefault();
				moveFocus(list, null, disableListWrap, allowFocusDisabledItem, nextItem);
			} else if (key === Key.End) {
				event.preventDefault();
				moveFocus(list, null, disableListWrap, allowFocusDisabledItem, previousItem);
			} else if (key.length === 1) {
				const criteria = textCriteriaRef.current;
				const lowerKey = key.toLowerCase();
				const currTime = performance.now();
				if (criteria.keys.length > 0) {
					// Reset
					if (currTime - criteria.lastTime > 500) {
						criteria.keys = [];
						criteria.repeating = true;
						criteria.previousKeyMatched = true;
					} else if (criteria.repeating && lowerKey !== criteria.keys[0]) {
						criteria.repeating = false;
					}
				}
				criteria.lastTime = currTime;
				criteria.keys.push(lowerKey);
				const keepFocusOnCurrent =
					currentFocus && !criteria.repeating && textCriteriaMatches(currentFocus, criteria);
				if (
					criteria.previousKeyMatched &&
					(keepFocusOnCurrent ||
						moveFocus(list, currentFocus, false, allowFocusDisabledItem, nextItem, criteria))
				) {
					event.preventDefault();
				} else {
					criteria.previousKeyMatched = false;
				}
			}

			if (isFunction(onKeyDown)) {
				onKeyDown(event);
			}
		}, []);

		let activeItemIndex = -1;

		React.Children.forEach(children, (child, index) => {
			if (!React.isValidElement(child)) {
				return;
			}

			if (!child.props.disabled) {
				if (variant === "selectedMenu" && child.props.selected) {
					activeItemIndex = index;
				} else if (activeItemIndex === -1) {
					activeItemIndex = index;
				}
			}
		});

		const items = React.Children.toArray(children).map((child, index) => {
			if (!React.isValidElement(child)) {
				return null;
			}

			if (index === activeItemIndex) {
				const newChildProps = {};
				if (autoFocusItem) {
					newChildProps["autoFocus"] = true;
				}
				if (child.props.tabIndex === undefined && variant === "selectedMenu") {
					newChildProps["tabIndex"] = 0;
				}

				return React.cloneElement(child, newChildProps);
			}

			return child;
		});

		if (className) {
			baseClass.add(className);
		}

		useEffect(() => {
			if (autoFocus) {
				listRef.current?.focus();
			}
		}, [ autoFocus ]);

		return (
			<List
				{...props}
				className={baseClass.toClassName()}
				Component="ul"
				onKeyDown={handleKeyDown}
				ref={forkRef}
			>
				{items}
			</List>
		);
	}
);

MenuList.defaultProps = {
	variant: "menu",
	role: "menu"
};

const MenuListMemo = React.memo(MenuList);

export { MenuListMemo as MenuList };
